import React, { useState, useContext, useEffect } from 'react';
import ChannelAdminContext from '../../ChannelAdminContext';
import { createProductMutation } from '../helpers/createProductMutation';
import styles from './CreateProductForm.scss';
import { CreateProductFormHeader } from './CreateProductFormHeader';
import { CreateProductFormInputFields } from './CreateProductFormInputFields';
import { CreateProductSummary } from './CreateProductSummary';
import { ErrorMessage } from 'components';
import { GraphQLError } from 'graphql';
import useUserLocale from 'hooks/useUserLocale';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import { NewProductDetails, TaxPolicy, ProductType } from 'application-types';
import { currencyToPaymentCurrency } from 'domains-billing-web-mappers';
import { TAX_VALUE_CONVERSION } from 'lane-shared/domains/billingPayments/constants';
import { AdminPage } from 'components/layout';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { isProductFormValid } from './helpers/isProductFormValid';
import { sanitizeProductValue } from './helpers/sanitizeProductValue';
import { Button, H4 } from 'design-system-web';
import {
  useBpMarkupChargeCodeEnabled,
  useBpAdminFeeChargeCodeEnabled,
  useProductPricing,
} from 'react-hooks';
import { CreditPricingSummary } from './CreditPricingSummary';

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
};

export function CreateProductForm({ breadcrumbs }: Props) {
  const { channel } = useContext(ChannelAdminContext);
  const { id }: { id: string } = useParams();
  const history = useHistory();
  const locale = useUserLocale();
  const isBpMarkupChargeCodeEnabled = useBpMarkupChargeCodeEnabled();
  const isBpAdminFeeChargeCodeEnabled = useBpAdminFeeChargeCodeEnabled();
  const [pageError, setPageError] = useState<
    readonly GraphQLError[] | Error | undefined
  >(undefined);

  const {
    paymentSettingsError,
    taxDetails,
    currency,
    markupChargeCodeId: rawDefaultMarkupChargeCodeId,
    adminFeeChargeCodeId: rawDefaultAdminFeeChargeCodeId,
    isAdminFeeTaxEnabled,
    adminFeeTax: channelAdminFeeTax,
  } = useBillingPaymentSettingsForChannel({ channelId: channel?._id });

  const inclusivePricing = taxDetails?.taxPolicy === TaxPolicy.INCLUSIVE;
  const { t } = useTranslation();

  const [newProductDetails, setNewProductDetails] = useState<NewProductDetails>(
    {
      productCategoryId: '',
      productCategoryName: '',
      productType: ProductType.PRODUCT_TYPE_SERVICE_LABOUR,
      chargeCodeId: undefined,
      name: '',
      description: '',
      rate: '',
      markup: undefined,
      tax: undefined,
      taxDefault: undefined,
      creditAmount: undefined,
    }
  );
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const paymentCurrency = currencyToPaymentCurrency(currency);
  const pricing = useProductPricing({
    priceInput: newProductDetails,
    currency: paymentCurrency,
    locale,
    inclusivePricing,
  });

  useEffect(() => {
    setNewProductDetails(prev => {
      let updates: Partial<NewProductDetails> = {};

      if (taxDetails && taxDetails.taxAmountValue) {
        const taxValue = {
          type: taxDetails.taxAmountType,
          value: (taxDetails.taxAmountValue / TAX_VALUE_CONVERSION).toString(),
        };

        updates = { ...updates, tax: taxValue, taxDefault: taxValue };
      }

      if (
        isAdminFeeTaxEnabled &&
        channelAdminFeeTax &&
        channelAdminFeeTax.rate > 0
      ) {
        const adminFeeTaxValue = {
          type: channelAdminFeeTax.taxAmountType,
          value: (channelAdminFeeTax.rate / TAX_VALUE_CONVERSION).toString(),
        };

        updates = { ...updates, adminFeeTaxDefault: adminFeeTaxValue };
      }

      if (Object.keys(updates).length === 0) return prev;

      return { ...prev, ...updates };
    });
  }, [taxDetails, isAdminFeeTaxEnabled, channelAdminFeeTax]);

  const isSaveButtonDisabled = (details = newProductDetails) => {
    return !isProductFormValid(details, {
      inclusivePricing,
      requireCategory: true,
      validateCreditAmount: true,
      isReportMarkupEnabled:
        isBpMarkupChargeCodeEnabled && !!rawDefaultMarkupChargeCodeId,
      isReportAdminFeeEnabled:
        isBpAdminFeeChargeCodeEnabled && !!rawDefaultAdminFeeChargeCodeId,
    });
  };

  const handleProductDetailsUpdate = (value: any, field: string) => {
    const emptyCategory =
      field === 'productType'
        ? { productCategoryId: '', productCategoryName: '' }
        : {};

    const sanitizedValue = sanitizeProductValue(value, field);

    setNewProductDetails(prev => {
      const updatedDetails = {
        ...prev,
        [field]: sanitizedValue,
        ...emptyCategory,
      };

      setIsSaveDisabled(isSaveButtonDisabled(updatedDetails));

      return updatedDetails;
    });
  };

  const handleSaveProduct = async () => {
    const {
      rate,
      grossTax,
      grossMarkup,
      listPrice,
      netPrice,
      grossAdminFee,
      grossAdminFeeTax,
    } = pricing;

    if (inclusivePricing) {
      newProductDetails.rate = rate.toString();
    }

    try {
      const result = await createProductMutation(
        newProductDetails,
        { grossTax, grossMarkup, listPrice, netPrice, grossAdminFee, grossAdminFeeTax },
        channel?._id || ''
      );

      if (result.errors) {
        setPageError(result.errors);
      }

      window.Toast.show(t('abp.productsServices.createProduct.successToast'));
      history.goBack();
    } catch (err: any) {
      window.Toast.show(t('abp.productsServices.createProduct.failToast'));
      setPageError(err);
    }
  };

  const handleCancel = async () => {
    history.goBack();
  };

  return (
    <AdminPage>
      <CreateProductFormHeader
        id={id}
        handleCancel={handleCancel}
        handleSaveProduct={handleSaveProduct}
        isSaveButtonDisabled={isSaveButtonDisabled}
        breadcrumbs={breadcrumbs}
      />
      <div className={styles.createProductPage}>
        <CreateProductFormInputFields
          productDetails={newProductDetails}
          updateProductDetails={handleProductDetailsUpdate}
          currency={paymentCurrency}
          inclusivePricing={inclusivePricing}
        />
        <div className={styles.productPricingSummary}>
          <H4>
            {t('abp.productsServices.createProduct.pricingSummary.title')}
          </H4>
          <CreateProductSummary
            productDetails={newProductDetails}
            currency={paymentCurrency}
            inclusivePricing={inclusivePricing}
            isSaveDisabled={isSaveDisabled}
            setIsSaveDisabled={setIsSaveDisabled}
          />
          {!!newProductDetails.creditAmount && (
            <CreditPricingSummary
              creditAmount={newProductDetails.creditAmount}
            />
          )}
        </div>
      </div>
      <div className={styles.buttonAndErrorGroup}>
        {pageError && <ErrorMessage error={pageError} />}
        {paymentSettingsError && <ErrorMessage error={paymentSettingsError} />}
        <div className={styles.buttonGroup}>
          <Button
            testId="saveNewProductButton"
            onClick={() => handleSaveProduct()}
            disabled={isSaveDisabled}
            size="large"
          >
            {t('abp.productsServices.createProduct.save')}
          </Button>
          <Button size="large" onClick={handleCancel} variant="secondary">
            {t('abp.cancel')}
          </Button>
        </div>
      </div>
    </AdminPage>
  );
}
